import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BarChart,
  CalendarToday,
  Close,
  ContentCopy,
  Download,
  Edit,
  FiberManualRecord,
  HowToVote,
  InfoOutlined,
  OpenInNew,
  Poll,
  QrCode2,
  RestartAlt,
  Visibility,
  Search,
  Lock,
  LockOpen,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';

import {
  darkGrey,
  headingBlue,
  maroon,
  mediumGrey,
  lightGrey,
  pureWhite,
  green,
  Donegreen,
  lightBlue,
  skyBlue,
} from '../../../assets/colors';
import { buttonStyle, button2Style, titleStyle } from '../../../utils/commonCSS';
import { updatePollStatus } from '../../../backend/backendApis';
import HttpService from '../../../backend/requestApis';
import AddPollTemplate from './AddPollTemplate';
import ElectionResult from '../ElectionResult/ElectionResult';
import Loader from '../../Loader';
import { showSnackBar } from '../../CustomComponents/CustomComponentsSlice';
import CustomTextField from '../../CustomTextField';

import logoBase64 from '../../../assets/images/WORK_logo.png';
import compLogoBase64 from '../../../assets/images/compassionMonth.png';

const tableHeaderCellStyle = {
  padding: '0.45rem 0.5rem',
  fontSize: '0.8rem',
  fontWeight: 700,
  textAlign: 'center',
  color: headingBlue,
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #D9D9D9',
  whiteSpace: 'nowrap',
};

const tableBodyCellStyle = {
  padding: '0.45rem 0.5rem',
  fontSize: '0.8rem',
  borderBottom: '1px solid #f1f5f9',
};

const scrollbarStyle = {
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
};

const formatPollCategory = (cat) => {
  if (!cat) return 'All WORKers';
  const lower = String(cat).toLowerCase().trim();
  if (lower === 'committed') return 'Committed WORKers';
  if (lower === 'uncommitted' || lower === 'noncommitted') return 'Supported WORKer';
  if (lower === 'all' || lower === 'all workers') return 'All WORKers';
  return cat;
};

export const isPollActive = (item) => {
  if (!item) return false;
  if (item.isClosed === true) return false;
  if (item.pollStatus === 'closed') return false;
  if (item.isActive === false) return false;
  if (item.pollStatus === 'open') return true;
  return Boolean(item.isActive);
};

const PollTable = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Dialog & Selection States
  const [editDialogStatus, setEditDialogStatus] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [electionResultDialog, setElectionResultDialog] = useState(false);
  const [confirmDialogStatus, setConfirmDialogStatus] = useState(false);
  const [targetStatusToSet, setTargetStatusToSet] = useState('closed'); // 'closed' | 'open'
  const [isLoading, setIsLoading] = useState(false);

  // Modals for QR Code and Full Details
  const [qrModalRow, setQrModalRow] = useState(null);
  const [detailsModalRow, setDetailsModalRow] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Role Scoping
  const isAdmin = localStorage.getItem('designation') === 'admin';
  const isStateHead = !isAdmin && localStorage.getItem('isStateHead') === 'true';
  const isDistrictHead = !isAdmin && localStorage.getItem('isDistrictHead') === 'true';
  const isHeadUser = isStateHead || isDistrictHead;

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All'); // 'All' | 'poll' | 'voting' | 'attendance'
  const [statusFilter, setStatusFilter] = useState('All'); // 'All' | 'active' | 'inactive'

  const updateConfirmDialogStatus = (value) => {
    setConfirmDialogStatus(value);
  };

  const updateEditDialogStatus = (value, refresh = false) => {
    setEditDialogStatus(value);
    if (refresh) {
      props.refreshTable();
    }
  };

  const updateElectionResultDialog = (value, refresh = false) => {
    setElectionResultDialog(value);
    if (refresh) {
      props.refreshTable();
    }
  };

  const updateTable = () => {
    props.refreshTable();
  };

  const handleUpdatePollStatus = (id, targetStatus) => {
    if (!id) return;
    setIsLoading(true);
    const options = {
      url: `${updatePollStatus}/${id}`,
      method: 'POST',
      payload: targetStatus ? { status: targetStatus } : {},
      headers: {
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null,
        },
      },
    };
    HttpService(options)
      .then((resp) => {
        setIsLoading(false);
        updateConfirmDialogStatus(false);
        const actionMsg =
          resp?.data?.message ||
          (targetStatus === 'closed'
            ? 'Successfully closed the poll.'
            : 'Successfully reopened the poll.');
        dispatch(showSnackBar({ message: actionMsg, severity: 'success' }));
        props.refreshTable();
      })
      .catch((err) => {
        setIsLoading(false);
        updateConfirmDialogStatus(false);
        console.log('Error in updating Poll Status: ', err);
        const status = err?.status || err?.response?.status;
        const errMsg = err?.response?.data?.description || err?.response?.data?.message;
        if (status === 401 || (!localStorage.getItem('token') && status === 403)) {
          localStorage.clear();
          navigate('/Login');
        } else {
          dispatch(
            showSnackBar({
              message: errMsg || 'Error in updating poll status. Admins only.',
              severity: 'error',
            })
          );
        }
      });
  };

  // Safe data array extraction
  const templatesList = useMemo(() => {
    if (Array.isArray(props?.data)) return props.data;
    if (Array.isArray(props?.data?.data)) return props.data.data;
    return [];
  }, [props?.data]);

  // Metric counts
  const totalCount = templatesList.length;
  const activeCount = templatesList.filter((t) => isPollActive(t)).length;
  const closedCount = templatesList.filter((t) => !isPollActive(t)).length;
  const electionCount = templatesList.filter((t) => t.templateType === 'voting').length;
  const pollCount = templatesList.filter((t) => t.templateType === 'poll').length;
  const restrictedCount = templatesList.filter((t) => {
    return Boolean(
      t.isRestricted ??
      t.restriction ??
      (t.WORKerCategory && t.WORKerCategory.toLowerCase() !== 'all')
    );
  }).length;

  // Filtered dataset
  const filteredData = useMemo(() => {
    return templatesList.filter((item) => {
      // Search by program name
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const name = (item.programName || '').toLowerCase();
        if (!name.includes(query)) return false;
      }
      // Type filter
      if (typeFilter !== 'All') {
        if (item.templateType !== typeFilter) return false;
      }
      // Status filter
      if (statusFilter === 'active') {
        if (!isPollActive(item)) return false;
      } else if (statusFilter === 'inactive') {
        if (isPollActive(item)) return false;
      }
      return true;
    });
  }, [templatesList, searchQuery, typeFilter, statusFilter]);

  // Helper URLs
  const getVotingUrl = (row) => {
    if (!row?._id) return '';
    const origin = window.location.origin;
    return row.templateType === 'voting'
      ? `${origin}/castvote/id/${row._id}`
      : `${origin}/castpoll/id/${row._id}`;
  };

  const handleCopyVotingLink = (row) => {
    const url = getVotingUrl(row);
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopiedId(row._id);
    dispatch(showSnackBar({ message: 'Voting link copied to clipboard!', severity: 'success' }));
    setTimeout(() => {
      setCopiedId((prev) => (prev === row._id ? null : prev));
    }, 2000);
  };

  const handleDownloadQR = (elementId, programName = 'Poll') => {
    const container = document.getElementById(elementId);
    if (!container) return;
    const svg = container.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngFile;
      downloadLink.download = `${programName.replace(/[^a-zA-Z0-9]/g, '_')}_QR.png`;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <>
      {isLoading && <Loader />}

      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: '0.2rem 0.5rem 0.5rem 0.5rem',
          boxSizing: 'border-box',
          backgroundColor: '#fff',
        }}
      >
        {/* ================================================================ */}
        {/* 1. TOP HEADER & METRICS BAR                                      */}
        {/* ================================================================ */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #D9D9D9',
            p: '0.35rem 0.6rem',
            mb: '0.35rem',
            borderRadius: '4px',
            flexShrink: 0,
            flexWrap: 'wrap',
            gap: '0.4rem',
            backgroundColor: pureWhite,
          }}
        >
          {/* Left: Title & Metric Badges */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: headingBlue }}>
              Polls & Elections Management
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Chip
                label={`Total: ${totalCount}`}
                size="small"
                sx={{
                  backgroundColor: '#f1f5f9',
                  color: headingBlue,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: '1.35rem',
                  borderRadius: '3px',
                }}
              />
              <Chip
                icon={<FiberManualRecord sx={{ fontSize: '0.55rem !important', color: '#16a34a !important' }} />}
                label={`Open: ${activeCount}`}
                size="small"
                sx={{
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: '1.35rem',
                  borderRadius: '3px',
                }}
              />
              <Chip
                icon={<FiberManualRecord sx={{ fontSize: '0.55rem !important', color: '#dc2626 !important' }} />}
                label={`Closed: ${closedCount}`}
                size="small"
                sx={{
                  backgroundColor: '#fee2e2',
                  color: '#991b1b',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: '1.35rem',
                  borderRadius: '3px',
                }}
              />
              <Chip
                label={`Elections: ${electionCount}`}
                size="small"
                sx={{
                  backgroundColor: '#ede9fe',
                  color: '#5b21b6',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: '1.35rem',
                  borderRadius: '3px',
                }}
              />
              <Chip
                label={`Restricted: ${restrictedCount}`}
                size="small"
                sx={{
                  backgroundColor: lightBlue,
                  color: headingBlue,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: '1.35rem',
                  borderRadius: '3px',
                }}
              />
            </Box>
          </Box>
        </Paper>

        {/* ================================================================ */}
        {/* 2. FILTER & SEARCH STRIP                                         */}
        {/* ================================================================ */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #D9D9D9',
            p: '0.25rem 0.6rem',
            mb: '0.35rem',
            borderRadius: '4px',
            flexShrink: 0,
            flexWrap: 'wrap',
            gap: '0.4rem',
            backgroundColor: pureWhite,
          }}
        >
          {/* Left: Program Type Tabs */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tabs
              value={typeFilter}
              onChange={(e, val) => setTypeFilter(val)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: 30,
                '& .MuiTab-root': {
                  textTransform: 'capitalize',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  minHeight: 30,
                  py: 0.1,
                  px: 0.8,
                  color: '#666',
                  '&.Mui-selected': { color: headingBlue },
                },
              }}
            >
              <Tab value="All" label={`All (${totalCount})`} />
              <Tab value="poll" label={`Polls (${pollCount})`} />
              <Tab value="voting" label={`Elections (${electionCount})`} />
              <Tab
                value="attendance"
                label={`Attendance (${templatesList.filter((t) => t.templateType === 'attendance').length})`}
              />
            </Tabs>
          </Box>

          {/* Right: Status Filter & Search Box */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            {/* Status Pills */}
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #D9D9D9', borderRadius: '4px', p: '0.1rem' }}>
              <Button
                size="small"
                onClick={() => setStatusFilter('All')}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: statusFilter === 'All' ? 700 : 500,
                  py: '0.1rem',
                  px: '0.45rem',
                  minWidth: 0,
                  borderRadius: '3px',
                  backgroundColor: statusFilter === 'All' ? headingBlue : 'transparent',
                  color: statusFilter === 'All' ? pureWhite : '#666',
                  '&:hover': { backgroundColor: statusFilter === 'All' ? headingBlue : '#f1f5f9' },
                }}
              >
                All Status
              </Button>
              <Button
                size="small"
                onClick={() => setStatusFilter('active')}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: statusFilter === 'active' ? 700 : 500,
                  py: '0.1rem',
                  px: '0.45rem',
                  minWidth: 0,
                  borderRadius: '3px',
                  backgroundColor: statusFilter === 'active' ? '#166534' : 'transparent',
                  color: statusFilter === 'active' ? pureWhite : '#666',
                  '&:hover': { backgroundColor: statusFilter === 'active' ? '#166534' : '#f1f5f9' },
                }}
              >
                Open ({activeCount})
              </Button>
              <Button
                size="small"
                onClick={() => setStatusFilter('inactive')}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: statusFilter === 'inactive' ? 700 : 500,
                  py: '0.1rem',
                  px: '0.45rem',
                  minWidth: 0,
                  borderRadius: '3px',
                  backgroundColor: statusFilter === 'inactive' ? maroon : 'transparent',
                  color: statusFilter === 'inactive' ? pureWhite : '#666',
                  '&:hover': { backgroundColor: statusFilter === 'inactive' ? maroon : '#f1f5f9' },
                }}
              >
                Closed ({closedCount})
              </Button>
            </Box>

            {/* Search Input */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
              <CustomTextField
                regular={true}
                placeHolder="Search by program name..."
                value={searchQuery}
                setFunc={setSearchQuery}
                width="13rem"
              />
              {searchQuery && (
                <Tooltip title="Clear Search">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ color: maroon, p: '0.15rem' }}
                  >
                    <RestartAlt fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Paper>

        {/* ================================================================ */}
        {/* 3. MAIN DATA TABLE (Sticky Header, Zero-Scroll Viewport Fill)     */}
        {/* ================================================================ */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            border: '1px solid #D9D9D9',
            overflowY: 'auto',
            borderRadius: '4px',
            backgroundColor: pureWhite,
            ...scrollbarStyle,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ ...tableHeaderCellStyle, width: '32%', textAlign: 'left' }}>
                  Program & Details
                </TableCell>
                <TableCell style={{ ...tableHeaderCellStyle, width: '80px', textAlign: 'center' }}>
                  QR Code
                </TableCell>
                <TableCell style={{ ...tableHeaderCellStyle, width: '18%', textAlign: 'center' }}>
                  Voting URL
                </TableCell>
                <TableCell style={{ ...tableHeaderCellStyle, width: '10%', textAlign: 'center' }}>
                  Status
                </TableCell>
                <TableCell style={{ ...tableHeaderCellStyle, width: '33%', textAlign: 'center' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => {
                  const isElection = row?.templateType === 'voting';
                  const isRestricted = Boolean(
                    row?.isRestricted ??
                    row?.restriction ??
                    (row?.WORKerCategory && row?.WORKerCategory.toLowerCase() !== 'all')
                  );
                  const isStatsEnabled = !isElection && isRestricted;
                  const isLiveResultDisabled = !isElection && isRestricted && isHeadUser;

                  let statsTooltip = 'View Poll Voting Analytics Screen';
                  if (isElection) {
                    statsTooltip = 'Stats not available for Elections';
                  } else if (!isRestricted) {
                    statsTooltip = 'Stats available only for Restricted Polls';
                  }

                  let resultsTooltip =
                    row.templateType === 'voting'
                      ? 'View Election Results Modal'
                      : 'View Live Poll Result Page';
                  if (isLiveResultDisabled) {
                    resultsTooltip = 'Live Results disabled for restricted polls (View Stats)';
                  }

                  const votingUrl = getVotingUrl(row);
                  const selectedLogo = row.logo === 'WORK' ? logoBase64 : compLogoBase64;

                  return (
                    <TableRow key={row._id || index} hover sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                      {/* ======================================== */}
                      {/* COLUMN 1: PROGRAM DETAILS & METADATA     */}
                      {/* ======================================== */}
                      <TableCell style={{ ...tableBodyCellStyle, textAlign: 'left', width: '32%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {/* Program Name & Type Chip */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <Typography
                              sx={{
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                color: headingBlue,
                                lineHeight: 1.25,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline', color: '#1d4877' },
                              }}
                              onClick={() => setDetailsModalRow(row)}
                            >
                              {row.programName || 'Untitled Program'}
                            </Typography>

                            {/* Type Chip */}
                            <Chip
                              size="small"
                              icon={
                                row.templateType === 'voting' ? (
                                  <HowToVote sx={{ fontSize: '0.8rem !important' }} />
                                ) : (
                                  <Poll sx={{ fontSize: '0.8rem !important' }} />
                                )
                              }
                              label={
                                row.templateType === 'voting'
                                  ? 'Election'
                                  : row.templateType === 'attendance'
                                    ? 'Attendance'
                                    : 'Poll'
                              }
                              sx={{
                                height: '1.25rem',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                backgroundColor:
                                  row.templateType === 'voting'
                                    ? '#ede9fe'
                                    : row.templateType === 'attendance'
                                      ? Donegreen
                                      : lightBlue,
                                color:
                                  row.templateType === 'voting'
                                    ? '#5b21b6'
                                    : row.templateType === 'attendance'
                                      ? green
                                      : headingBlue,
                                borderRadius: '3px',
                              }}
                            />

                            <Tooltip title="Click to view full questions & details">
                              <IconButton
                                size="small"
                                onClick={() => setDetailsModalRow(row)}
                                sx={{ color: headingBlue, p: '0.1rem' }}
                              >
                                <InfoOutlined sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {/* Secondary Meta Row: Wing, Access, WORKer Category, Date */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={`Wing: ${row.wing === 'men' ? 'Men' : row.wing === 'women' ? 'Women' : 'All'}`}
                              sx={{
                                height: '1.15rem',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                                borderRadius: '3px',
                              }}
                            />

                            <Chip
                              size="small"
                              label={row.outputType === 'private' ? 'Private' : 'Public'}
                              sx={{
                                height: '1.15rem',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                backgroundColor: row.outputType === 'private' ? '#fee2e2' : '#dcfce7',
                                color: row.outputType === 'private' ? '#991b1b' : '#166534',
                                borderRadius: '3px',
                              }}
                            />

                            {/* WORKer Restriction Badge */}
                            {row.WORKerCategory && row.WORKerCategory.toLowerCase() !== 'all' ? (
                              <Chip
                                size="small"
                                label={`Restricted: ${formatPollCategory(row.WORKerCategory)}`}
                                sx={{
                                  height: '1.15rem',
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  borderRadius: '3px',
                                }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                label="All WORKers"
                                sx={{
                                  height: '1.15rem',
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  backgroundColor: '#f1f5f9',
                                  color: '#64748b',
                                  borderRadius: '3px',
                                }}
                              />
                            )}

                            {/* Date */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.2rem', ml: '0.2rem' }}>
                              <CalendarToday sx={{ fontSize: '0.7rem', color: '#666' }} />
                              <Typography sx={{ fontSize: '0.7rem', color: '#666', fontWeight: 500 }}>
                                {row.programDate ? row.programDate.split('T')[0] : '-'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* ======================================== */}
                      {/* COLUMN 2: QR CODE THUMBNAIL (AFTER DETAILS) */}
                      {/* ======================================== */}
                      <TableCell style={{ ...tableBodyCellStyle, textAlign: 'center', width: '80px' }}>
                        <Tooltip title="Click to view & download full-size QR code">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              p: '0.2rem',
                              borderRadius: '4px',
                              border: '1px solid #e2e8f0',
                              backgroundColor: pureWhite,
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                borderColor: headingBlue,
                                backgroundColor: lightBlue,
                                transform: 'scale(1.04)',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                              },
                            }}
                            onClick={() => setQrModalRow(row)}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 0,
                              }}
                            >
                              <QRCodeSVG
                                value={votingUrl}
                                size={44}
                                fgColor={headingBlue}
                                imageSettings={{
                                  src: selectedLogo,
                                  height: 9,
                                  width: 13,
                                  excavate: true,
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '0.58rem',
                                color: headingBlue,
                                fontWeight: 700,
                                mt: '0.1rem',
                                lineHeight: 1,
                              }}
                            >
                              View QR
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>

                      {/* ======================================== */}
                      {/* COLUMN 3: DIRECT VOTING URL              */}
                      {/* ======================================== */}
                      <TableCell style={tableBodyCellStyle}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            maxWidth: '220px',
                            margin: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              backgroundColor: '#f8fafc',
                              border: '1px solid #D9D9D9',
                              borderRadius: '4px',
                              px: '0.4rem',
                              py: '0.2rem',
                              textAlign: 'left',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.68rem',
                                color: headingBlue,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                              {votingUrl}
                            </Typography>
                          </Box>

                          <Tooltip title={copiedId === row._id ? 'Copied!' : 'Copy Voting Link'}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyVotingLink(row)}
                              sx={{
                                p: '0.2rem',
                                color: headingBlue,
                                backgroundColor: copiedId === row._id ? Donegreen : '#f1f5f9',
                                border: '1px solid #D9D9D9',
                                borderRadius: '4px',
                                '&:hover': { backgroundColor: lightBlue },
                              }}
                            >
                              <ContentCopy sx={{ fontSize: '0.82rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* ======================================== */}
                      {/* COLUMN 4: STATUS (OPEN / CLOSED)         */}
                      {/* ======================================== */}
                      <TableCell style={{ ...tableBodyCellStyle, textAlign: 'center', width: '10%' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.25rem',
                          }}
                        >
                          <Chip
                            size="small"
                            icon={
                              <FiberManualRecord
                                sx={{
                                  fontSize: '0.55rem !important',
                                  color: isPollActive(row) ? '#16a34a !important' : '#dc2626 !important',
                                }}
                              />
                            }
                            label={isPollActive(row) ? 'Open' : 'Closed'}
                            sx={{
                              height: '1.25rem',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              backgroundColor: isPollActive(row) ? '#dcfce7' : '#fee2e2',
                              color: isPollActive(row) ? '#166534' : '#991b1b',
                              border: `1px solid ${isPollActive(row) ? '#86efac' : '#fca5a5'}`,
                              borderRadius: '12px',
                            }}
                          />

                          {isAdmin && (
                            <Tooltip
                              title={
                                isPollActive(row)
                                  ? 'Poll is currently Open. Click switch to Close Poll'
                                  : 'Poll is currently Closed. Click switch to Reopen Poll'
                              }
                            >
                              <span>
                                <Switch
                                  size="small"
                                  checked={isPollActive(row)}
                                  onChange={() => {
                                    setSelectedRow(row);
                                    setTargetStatusToSet(isPollActive(row) ? 'closed' : 'open');
                                    updateConfirmDialogStatus(true);
                                  }}
                                  sx={{
                                    width: 38,
                                    height: 22,
                                    padding: 0,
                                    display: 'flex',
                                    '& .MuiSwitch-switchBase': {
                                      padding: '2px',
                                      color: '#ffffff',
                                      '&.Mui-checked': {
                                        transform: 'translateX(16px)',
                                        color: '#ffffff !important',
                                        '& + .MuiSwitch-track': {
                                          backgroundColor: '#16a34a !important',
                                          opacity: '1 !important',
                                          border: 'none',
                                        },
                                      },
                                    },
                                    '& .MuiSwitch-thumb': {
                                      width: 18,
                                      height: 18,
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                      backgroundColor: '#ffffff',
                                    },
                                    '& .MuiSwitch-track': {
                                      borderRadius: 11,
                                      backgroundColor: '#dc2626 !important',
                                      opacity: '1 !important',
                                      border: 'none',
                                      boxSizing: 'border-box',
                                    },
                                  }}
                                />
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* ======================================== */}
                      {/* COLUMN 5: EXPLANATORY ACTION BUTTONS     */}
                      {/* ======================================== */}
                      <TableCell style={tableBodyCellStyle}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.35rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          {/* 1. VOTE FORM BUTTON */}
                          {/* <Tooltip
                            title={
                              row.templateType === 'voting'
                                ? 'Open Voter Form in new tab'
                                : 'Open Poll Form in new tab'
                            }
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<OpenInNew sx={{ fontSize: '0.85rem !important' }} />}
                              onClick={() => window.open(votingUrl, '_blank', 'noopener,noreferrer')}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                py: '0.15rem',
                                px: '0.45rem',
                                minWidth: 0,
                                height: '1.65rem',
                                color: headingBlue,
                                borderColor: '#cbd5e1',
                                backgroundColor: '#f8fafc',
                                '&:hover': {
                                  backgroundColor: lightBlue,
                                  borderColor: headingBlue,
                                },
                              }}
                            >
                              Vote Form
                            </Button>
                          </Tooltip> */}

                          {/* 2. RESULTS BUTTON */}
                          <Tooltip title={resultsTooltip}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={isLiveResultDisabled}
                                startIcon={<Visibility sx={{ fontSize: '0.85rem !important' }} />}
                                onClick={() => {
                                  if (isLiveResultDisabled) return;
                                  if (row.templateType === 'voting') {
                                    setSelectedRow(row);
                                    updateElectionResultDialog(true);
                                  } else {
                                    navigate(`/PollResult/${row._id}/${row?.logo || 'WORK'}`);
                                  }
                                }}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.72rem',
                                  fontWeight: 600,
                                  py: '0.15rem',
                                  px: '0.45rem',
                                  minWidth: 0,
                                  height: '1.65rem',
                                  color: isLiveResultDisabled ? '#94a3b8' : '#047857',
                                  borderColor: isLiveResultDisabled ? '#e2e8f0' : '#a7f3d0',
                                  backgroundColor: isLiveResultDisabled ? '#f8fafc' : '#ecfdf5',
                                  '&:hover': {
                                    backgroundColor: isLiveResultDisabled ? '#f8fafc' : '#d1fae5',
                                    borderColor: isLiveResultDisabled ? '#e2e8f0' : '#059669',
                                  },
                                  '&.Mui-disabled': {
                                    color: '#94a3b8',
                                    borderColor: '#e2e8f0',
                                    backgroundColor: '#f8fafc',
                                  },
                                }}
                              >
                                {row.templateType === 'voting' ? 'Results' : 'Live Results'}
                              </Button>
                            </span>
                          </Tooltip>

                          {/* 3. STATS BUTTON (FOR RESTRICTED POLLS) */}
                          <Tooltip title={statsTooltip}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!isStatsEnabled}
                                startIcon={<BarChart sx={{ fontSize: '0.85rem !important' }} />}
                                onClick={() => {
                                  if (isStatsEnabled && props?.onViewStats) {
                                    props.onViewStats(row);
                                  }
                                }}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.72rem',
                                  fontWeight: 600,
                                  py: '0.15rem',
                                  px: '0.45rem',
                                  minWidth: 0,
                                  height: '1.65rem',
                                  color: isStatsEnabled ? '#6b21a8' : '#94a3b8',
                                  borderColor: isStatsEnabled ? '#e9d5ff' : '#e2e8f0',
                                  backgroundColor: isStatsEnabled ? '#faf5ff' : '#f8fafc',
                                  '&:hover': {
                                    backgroundColor: '#f3e8ff',
                                    borderColor: '#9333ea',
                                  },
                                  '&.Mui-disabled': {
                                    color: '#94a3b8',
                                    borderColor: '#e2e8f0',
                                    backgroundColor: '#f8fafc',
                                  },
                                }}
                              >
                                Stats
                              </Button>
                            </span>
                          </Tooltip>

                          {/* 4. ADMIN ONLY: CLOSE / REOPEN POLL BUTTON */}
                          {isAdmin && (
                            isPollActive(row) ? (
                              <Tooltip title="Close Poll: Voters will no longer be able to submit responses">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Lock sx={{ fontSize: '0.82rem !important' }} />}
                                  onClick={() => {
                                    setSelectedRow(row);
                                    setTargetStatusToSet('closed');
                                    updateConfirmDialogStatus(true);
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    py: '0.15rem',
                                    px: '0.45rem',
                                    minWidth: 0,
                                    height: '1.65rem',
                                    color: '#b91c1c',
                                    borderColor: '#fca5a5',
                                    backgroundColor: '#fef2f2',
                                    '&:hover': {
                                      backgroundColor: '#fee2e2',
                                      borderColor: '#dc2626',
                                    },
                                  }}
                                >
                                  Close Poll
                                </Button>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Reopen Poll: Allow voters to submit responses">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<LockOpen sx={{ fontSize: '0.82rem !important' }} />}
                                  onClick={() => {
                                    setSelectedRow(row);
                                    setTargetStatusToSet('open');
                                    updateConfirmDialogStatus(true);
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    py: '0.15rem',
                                    px: '0.45rem',
                                    minWidth: 0,
                                    height: '1.65rem',
                                    color: '#15803d',
                                    borderColor: '#86efac',
                                    backgroundColor: '#f0fdf4',
                                    '&:hover': {
                                      backgroundColor: '#dcfce7',
                                      borderColor: '#16a34a',
                                    },
                                  }}
                                >
                                  Reopen Poll
                                </Button>
                              </Tooltip>
                            )
                          )}

                          {/* 5. EDIT BUTTON */}
                          <Tooltip
                            title={isPollActive(row) ? 'Cannot Edit Active/Open Poll' : 'Edit Poll Questions & Details'}
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={isPollActive(row)}
                                startIcon={<Edit sx={{ fontSize: '0.82rem !important' }} />}
                                onClick={() => {
                                  setSelectedRow(row);
                                  updateEditDialogStatus(true);
                                }}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.72rem',
                                  fontWeight: 600,
                                  py: '0.15rem',
                                  px: '0.45rem',
                                  minWidth: 0,
                                  height: '1.65rem',
                                  color: '#b45309',
                                  borderColor: '#fde68a',
                                  backgroundColor: '#fffbeb',
                                  '&:hover': {
                                    backgroundColor: '#fef3c7',
                                    borderColor: '#d97706',
                                  },
                                  '&.Mui-disabled': {
                                    color: '#94a3b8',
                                    borderColor: '#e2e8f0',
                                    backgroundColor: '#f8fafc',
                                  },
                                }}
                              >
                                Edit
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} style={{ ...tableBodyCellStyle, textAlign: 'center', padding: '2rem' }}>
                    <Typography style={{ color: maroon, fontSize: '0.9rem', fontWeight: 600 }}>
                      No Polls or Elections match your current filters.
                    </Typography>
                    {(searchQuery || typeFilter !== 'All' || statusFilter !== 'All') && (
                      <Button
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                          setTypeFilter('All');
                          setStatusFilter('All');
                        }}
                        sx={{ mt: 1, textTransform: 'none', fontSize: '0.78rem', color: headingBlue }}
                      >
                        Reset All Filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ================================================================ */}
      {/* 4. QR CODE PREVIEW & DOWNLOAD MODAL                              */}
      {/* ================================================================ */}
      {qrModalRow && (
        <Dialog
          open={Boolean(qrModalRow)}
          onClose={() => setQrModalRow(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: '8px', overflow: 'hidden' },
          }}
        >
          <DialogTitle sx={{ ...titleStyle, justifyContent: 'space-between', pr: 1.5, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCode2 sx={{ fontSize: '1.2rem' }} />
              <Typography sx={{ fontSize: '0.98rem', fontWeight: 600 }}>Scan to Vote</Typography>
            </Box>
            <IconButton size="small" onClick={() => setQrModalRow(null)} sx={{ color: pureWhite }}>
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 2.5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: headingBlue, mb: 0.5 }}>
              {qrModalRow.programName}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1.5 }}>
              <Chip
                size="small"
                label={
                  qrModalRow.templateType === 'voting'
                    ? 'Election'
                    : qrModalRow.templateType === 'attendance'
                      ? 'Attendance'
                      : 'Poll'
                }
                sx={{
                  height: '1.25rem',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  backgroundColor: qrModalRow.templateType === 'voting' ? '#ede9fe' : lightBlue,
                  color: qrModalRow.templateType === 'voting' ? '#5b21b6' : headingBlue,
                }}
              />
              <Chip
                size="small"
                label={isPollActive(qrModalRow) ? 'Open' : 'Closed'}
                sx={{
                  height: '1.25rem',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  backgroundColor: isPollActive(qrModalRow) ? '#dcfce7' : '#fee2e2',
                  color: isPollActive(qrModalRow) ? '#166534' : '#991b1b',
                }}
              />
            </Box>

            {/* High-Resolution QR Container */}
            <Box
              id="modal-qr-container"
              sx={{
                display: 'inline-flex',
                p: 1.5,
                border: '1px solid #D9D9D9',
                borderRadius: '8px',
                backgroundColor: pureWhite,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                mb: 1.5,
              }}
            >
              <QRCodeSVG
                value={getVotingUrl(qrModalRow)}
                size={180}
                fgColor={headingBlue}
                imageSettings={{
                  src: qrModalRow.logo === 'WORK' ? logoBase64 : compLogoBase64,
                  height: 36,
                  width: 52,
                  excavate: true,
                }}
              />
            </Box>

            {/* Voting URL snippet box */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f8fafc',
                border: '1px solid #D9D9D9',
                borderRadius: '4px',
                p: '0.2rem 0.5rem',
                mb: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: headingBlue,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '260px',
                }}
              >
                {getVotingUrl(qrModalRow)}
              </Typography>
              <Tooltip title="Copy Link">
                <IconButton
                  size="small"
                  onClick={() => handleCopyVotingLink(qrModalRow)}
                  sx={{ color: headingBlue, p: '0.1rem' }}
                >
                  <ContentCopy sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<Download sx={{ fontSize: '0.9rem !important' }} />}
                onClick={() => handleDownloadQR('modal-qr-container', qrModalRow.programName)}
                sx={{ ...buttonStyle, textTransform: 'none', fontSize: '0.78rem' }}
              >
                Download QR
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNew sx={{ fontSize: '0.9rem !important' }} />}
                onClick={() => window.open(getVotingUrl(qrModalRow), '_blank', 'noopener,noreferrer')}
                sx={{ ...button2Style, textTransform: 'none', fontSize: '0.78rem' }}
              >
                Open Form
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* ================================================================ */}
      {/* 5. FULL DETAILS & QUESTIONS MODAL                                */}
      {/* ================================================================ */}
      {detailsModalRow && (
        <Dialog
          open={Boolean(detailsModalRow)}
          onClose={() => setDetailsModalRow(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: '8px', overflow: 'hidden' },
          }}
        >
          <DialogTitle sx={{ ...titleStyle, justifyContent: 'space-between', pr: 1.5, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoOutlined sx={{ fontSize: '1.2rem' }} />
              <Typography sx={{ fontSize: '0.98rem', fontWeight: 600 }}>
                Program Details: {detailsModalRow.programName}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setDetailsModalRow(null)} sx={{ color: pureWhite }}>
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              p: 2,
              maxHeight: '70vh',
              overflowY: 'auto',
              ...scrollbarStyle,
            }}
          >
            {/* Top summary grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
                p: 1.5,
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                mb: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>TYPE</Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: headingBlue }}>
                  {detailsModalRow.templateType === 'voting'
                    ? 'Election'
                    : detailsModalRow.templateType === 'attendance'
                      ? 'Attendance'
                      : 'Poll'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>STATUS</Typography>
                <Typography
                  sx={{
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    color: isPollActive(detailsModalRow) ? '#166534' : '#991b1b',
                  }}
                >
                  {isPollActive(detailsModalRow) ? 'Open (Live)' : 'Closed'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>PROGRAM DATE</Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: headingBlue }}>
                  {detailsModalRow.programDate ? detailsModalRow.programDate.split('T')[0] : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
                  WING & VISIBILITY
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: headingBlue }}>
                  {detailsModalRow.wing === 'men'
                    ? 'Men'
                    : detailsModalRow.wing === 'women'
                      ? 'Women'
                      : 'All Wings'}{' '}
                  • {detailsModalRow.outputType === 'private' ? 'Private' : 'Public'}
                </Typography>
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
                  WORKER RESTRICTION
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: headingBlue }}>
                  {formatPollCategory(detailsModalRow.WORKerCategory)}
                </Typography>
              </Box>
            </Box>

            {/* Questions / Options section */}
            {detailsModalRow.questions && detailsModalRow.questions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: headingBlue, mb: 1 }}>
                  Questions ({detailsModalRow.questions.length})
                </Typography>
                {detailsModalRow.questions.map((q, idx) => (
                  <Paper
                    key={idx}
                    sx={{ p: 1.5, mb: 1, border: '1px solid #D9D9D9', backgroundColor: pureWhite }}
                  >
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: headingBlue }}>
                      Q{idx + 1}. {q.title || q.question}
                    </Typography>
                    {q.options && Array.isArray(q.options) && (
                      <Box sx={{ mt: 0.5, pl: 1 }}>
                        {q.options.map((opt, oIdx) => (
                          <Typography key={oIdx} sx={{ fontSize: '0.76rem', color: '#475569' }}>
                            • {typeof opt === 'string' ? opt : opt?.text || opt?.title || JSON.stringify(opt)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            {/* Election details (Candidates / Commission) */}
            {detailsModalRow.templateType === 'voting' && (
              <Box sx={{ mb: 2 }}>
                {detailsModalRow.candidatesList && detailsModalRow.candidatesList.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: headingBlue, mb: 0.5 }}>
                      Candidates ({detailsModalRow.candidatesList.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {detailsModalRow.candidatesList.map((c, cIdx) => (
                        <Chip
                          key={cIdx}
                          label={c.name || c.contactNumber || `Candidate ${cIdx + 1}`}
                          size="small"
                          sx={{ backgroundColor: '#f1f5f9', fontSize: '0.74rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {detailsModalRow.electionCommisionsList && detailsModalRow.electionCommisionsList.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: headingBlue, mb: 0.5 }}>
                      Election Commission ({detailsModalRow.electionCommisionsList.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {detailsModalRow.electionCommisionsList.map((ec, ecIdx) => (
                        <Chip
                          key={ecIdx}
                          label={ec.name || ec.contactNumber || `EC ${ecIdx + 1}`}
                          size="small"
                          sx={{ backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '0.74rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ================================================================ */}
      {/* 6. CONFIRM POLL STATUS (CLOSE / REOPEN / ACTIVATE) DIALOG        */}
      {/* ================================================================ */}
      <Dialog
        maxWidth="sm"
        fullWidth
        open={confirmDialogStatus}
        onClose={() => updateConfirmDialogStatus(false)}
        PaperProps={{ sx: { borderRadius: '6px' } }}
      >
        <DialogTitle style={{ ...titleStyle, fontSize: '1.1rem' }}>
          {targetStatusToSet === 'closed' ? 'Confirm Close Poll' : 'Confirm Reopen Poll'}
        </DialogTitle>
        <DialogContent style={{ marginTop: '0.5rem', padding: '1.2rem' }}>
          <Typography style={{ fontSize: '0.95rem', color: targetStatusToSet === 'closed' ? maroon : headingBlue, fontWeight: 600 }}>
            {targetStatusToSet === 'closed'
              ? `Are you sure you want to close "${selectedRow?.programName || 'this program'}"? Voters will no longer be able to submit responses.`
              : `Are you sure you want to reopen "${selectedRow?.programName || 'this program'}"? Voters will be able to submit responses again.`}
          </Typography>
          <Typography style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.35rem' }}>
            {targetStatusToSet === 'closed'
              ? 'Closing this poll immediately prevents any new responses from being cast on the voting screen.'
              : 'Reopening this poll reactivates the voting screen and allows participants to submit responses.'}
          </Typography>
          <Box style={{ marginTop: '1.2rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button
              style={{ ...buttonStyle, backgroundColor: darkGrey }}
              onClick={() => updateConfirmDialogStatus(false)}
            >
              Cancel
            </Button>
            <Button
              style={{
                ...buttonStyle,
                backgroundColor: targetStatusToSet === 'closed' ? maroon : '#166534',
              }}
              onClick={() => handleUpdatePollStatus(selectedRow?._id, targetStatusToSet)}
            >
              {targetStatusToSet === 'closed' ? 'Close Poll' : 'Reopen Poll'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ================================================================ */}
      {/* 7. CHILD MODALS: EDIT POLL TEMPLATE & ELECTION RESULT            */}
      {/* ================================================================ */}
      <AddPollTemplate
        dialogStatus={editDialogStatus}
        updateDialogStatus={updateEditDialogStatus}
        edit={true}
        data={selectedRow}
      />
      <ElectionResult
        dialoagStatus={electionResultDialog}
        updateDialogStatus={updateElectionResultDialog}
        data={selectedRow}
        updateTable={updateTable}
      />
    </>
  );
};

export default PollTable;
